import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';

import { HIGHLIGHT_WORDS_FOR_QUESTION } from '@/constant';
import { LessonTitle } from '@/routes/_embaUserLayout/-components/LessonTitle';
import { useLeaveBlocker } from '@/routes/_embaUserLayout/-hooks/useLeaveBlocker';
import { highlightText } from '@/utils/highlighter';
import { safeHtml } from '@/utils/safeHtml';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { FreeInputQuestion } from './-components/FreeInputQuestion';
import { SelectQuestion } from './-components/SelectQuestion';

import nextButton from '@/assets/images/next.svg';
import nextButtonDisabled from '@/assets/images/next_disabled.svg';
import prevButton from '@/assets/images/prev.svg';
import prevButtonDisabled from '@/assets/images/prev_disabled.svg';
import { ImageDialog } from '@/components/ImageDialog';

import styles from './index.module.scss';

export const COURSE_LESSON_PRACTICE_FRAGMENT = graphql(/* GraphQL */ `
  fragment EmbaCourseLessonPractice on CourseLessonPractice {
    id
    questions {
      id
      ordinal
      practiceQuestion {
        ... on PracticeSelectQuestion {
          id
          content
          attachmentImageUrl
          exampleContent
          exampleAttachmentImageUrl
          userAnswer {
            id
          }
        }
        ... on PracticeFreeInputQuestion {
          id
          content
          exampleContent
          attachmentImageUrl
          exampleAttachmentImageUrl
          userAnswer {
            id
          }
        }
        ...EmbaCourseLessonPracticeSelectQuestion
        ...EmbaCourseLessonPracticeFreeInputQuestion
      }
    }
  }
`);

export type CourseLessonPracticePageProps = {
  content: FragmentType<typeof COURSE_LESSON_PRACTICE_FRAGMENT>;
  lessonName: string;
  chapterName: string;
  onEmbaCompleteCourseLesson: () => Promise<void>;
  answerPracticeFreeInputQuestion: (
    embaPracticeFreeInputQuestionId: number,
    embaPracticeFreeInputQuestionAnswerContent: string,
  ) => Promise<void>;
  answerPracticeSelectQuestion: (
    embaPracticeSelectQuestionId: number,
    embaPracticeSelectQuestionAnswerChoiceId: number,
  ) => Promise<void>;
  isAnsweredModalOpen: boolean;
  closeAnsweredModal: () => void;
  scrollToExplanation: () => void;
  answerPracticeFreeInputQuestionLoading: boolean;
  answerPracticeSelectQuestionLoading: boolean;
};

export const CourseLessonPracticePageComponent = ({
  content,
  lessonName,
  chapterName,
  onEmbaCompleteCourseLesson,
  answerPracticeFreeInputQuestion,
  answerPracticeSelectQuestion,
  isAnsweredModalOpen,
  closeAnsweredModal,
  scrollToExplanation,
  answerPracticeFreeInputQuestionLoading,
  answerPracticeSelectQuestionLoading,
}: CourseLessonPracticePageProps) => {
  const embaCourseLessonPracticeData = useFragment(
    COURSE_LESSON_PRACTICE_FRAGMENT,
    content,
  );

  const { questions } = embaCourseLessonPracticeData;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const currentQuestion = questions[currentQuestionIndex].practiceQuestion;
  const [answer, setAnswer] = useState('');
  const isAlreadyAnswered = currentQuestion.userAnswer !== null; // 回答済みかどうかの状態
  const allAnswered = questions.every(
    (question) => question.practiceQuestion.userAnswer !== null,
  );

  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const exampleContentScrollRef = useRef<HTMLDivElement>(null);

  const handleAnswerPracticeFreeInputQuestion = async () => {
    // hooks側でerrorをthrowしているので、ここでのエラーハンドリングは不要
    await answerPracticeFreeInputQuestion(Number(currentQuestion.id), answer);
  };

  const handleAnswerPracticeSelectQuestion = async () => {
    // hooks側でerrorをthrowしているので、ここでのエラーハンドリングは不要
    await answerPracticeSelectQuestion(
      Number(currentQuestion.id),
      Number(answer),
    );
  };

  const { onBlocking, unBlocking, initBlocking } = useLeaveBlocker();
  useEffect(() => {
    allAnswered ? unBlocking() : onBlocking();
  }, [allAnswered, unBlocking, onBlocking]);
  initBlocking();

  const handleNext = () => {
    unBlocking();
    closeAnsweredModal();
    if (isLastQuestion && allAnswered) {
      onEmbaCompleteCourseLesson();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setAnswer('');
      scrollUpContent();
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      scrollUpContent();
    }
  };

  const scrollUpContent = () => {
    window.scrollTo({
      top: 0,
    });
    if (exampleContentScrollRef.current) {
      exampleContentScrollRef.current.scrollTop = 0;
    }
  };

  return (
    <div>
      <LessonTitle lessonName={lessonName} chapterName={chapterName} />
      <div className={styles.practiceWrapper}>
        <div className={styles.questionArea}>
          <div className={styles.questionContentArea}>
            <div className={styles.questionHeader}>
              <div className={styles.questionLabel}>問</div>

              <div className={styles.questionHeaderRight}>
                <div className={styles.questionCount}>
                  {currentQuestionIndex + 1}/{questions.length}
                </div>
                <div className={styles.questionControll}>
                  {currentQuestionIndex === 0 ? (
                    <img src={prevButtonDisabled} alt="" />
                  ) : (
                    <button onClick={handlePrev} type="button">
                      <img src={prevButton} alt="" />
                    </button>
                  )}
                  {isLastQuestion || !isAlreadyAnswered ? (
                    <img src={nextButtonDisabled} alt="" />
                  ) : (
                    <button onClick={handleNext} type="button">
                      <img src={nextButton} alt="" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <p
              className={styles.questionContent}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: サニタイズ済みのHTMLを表示するため
              dangerouslySetInnerHTML={{
                __html: safeHtml(
                  highlightText(
                    currentQuestion.content,
                    HIGHLIGHT_WORDS_FOR_QUESTION,
                  ),
                ),
              }}
            />

            {currentQuestion.attachmentImageUrl && (
              <div className={styles.questionContentImage}>
                <ImageDialog imageUrl={currentQuestion.attachmentImageUrl} />
              </div>
            )}
            {/* eslint-disable-next-line no-underscore-dangle */}
            {currentQuestion.__typename === 'PracticeFreeInputQuestion' && (
              <FreeInputQuestion
                question={currentQuestion}
                answer={answer}
                setAnswer={setAnswer}
                isAlreadyAnswered={isAlreadyAnswered}
                handleAnswerPracticeFreeInputQuestion={
                  handleAnswerPracticeFreeInputQuestion
                }
                handleNext={handleNext}
                isLastQuestion={isLastQuestion}
                allAnswered={allAnswered}
                isAnsweredModalOpen={isAnsweredModalOpen}
                closeAnsweredModal={closeAnsweredModal}
                scrollToExplanation={scrollToExplanation}
                answerPracticeFreeInputQuestionLoading={
                  answerPracticeFreeInputQuestionLoading
                }
              />
            )}

            {/* eslint-disable-next-line no-underscore-dangle */}
            {currentQuestion.__typename === 'PracticeSelectQuestion' && (
              <>
                <SelectQuestion
                  question={currentQuestion}
                  answer={answer}
                  setAnswer={setAnswer}
                  isAlreadyAnswered={isAlreadyAnswered}
                  handleAnswerPracticeSelectQuestion={
                    handleAnswerPracticeSelectQuestion
                  }
                  handleNext={handleNext}
                  isLastQuestion={isLastQuestion}
                  allAnswered={allAnswered}
                  isAnsweredModalOpen={isAnsweredModalOpen}
                  closeAnsweredModal={closeAnsweredModal}
                  scrollToExplanation={scrollToExplanation}
                  answerPracticeSelectQuestionLoading={
                    answerPracticeSelectQuestionLoading
                  }
                />
              </>
            )}
          </div>
          {currentQuestion.exampleContent && (
            <div className={styles.exampleContent}>
              <div className={styles.exampleContentInner}>
                <div className={styles.exampleLabel}>事例</div>
                <div
                  className={styles.exampleContentScrollArea}
                  ref={exampleContentScrollRef}
                >
                  <p
                    className={styles.exampleContentText}
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: サニタイズ済みのHTMLを表示するため
                    dangerouslySetInnerHTML={{
                      __html: safeHtml(currentQuestion.exampleContent),
                    }}
                  />
                  {currentQuestion.exampleAttachmentImageUrl && (
                    <div className={styles.exampleContentImage}>
                      <ImageDialog
                        imageUrl={currentQuestion.exampleAttachmentImageUrl}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
